/**
 * <skin-concerns-nav>
 *
 * Wraps the Skin Concerns navigator tile grid + shared panel container.
 * All six panels are already server-rendered inside #ConcernsRoutes; this
 * element only ever toggles which one is visible and where the shared
 * container sits in the DOM — it never fetches or injects markup.
 *
 * Desktop/tablet: the routes container stays after the tile grid.
 * Mobile (<=560px): the routes container moves to sit right after the
 * active tile, so it reads as an accordion joined to that tile.
 *
 * URL hash support: #concerns or #concerns/<key> opens (and, for a keyed
 * hash, scrolls to) the matching concern. Tapping a tile updates the hash
 * via replaceState without adding a history entry.
 */
if (!customElements.get('skin-concerns-nav')) {
  class SkinConcernsNav extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('#ConcernsNav');
      this.routes = this.querySelector('#ConcernsRoutes');
      if (!this.nav || !this.routes) return;

      this.buttons = Array.from(this.nav.querySelectorAll('button[data-concern-key]'));
      this.panels = Array.from(this.routes.querySelectorAll('[data-concern-key]'));
      this.mq = window.matchMedia('(max-width: 560px)');

      this.nav.addEventListener('click', this.onTileClick.bind(this));
      this.mq.addEventListener('change', () => {
        const activeKey = this.activeKey();
        this.place(activeKey);
      });

      window.addEventListener('hashchange', this.onHashChange.bind(this));

      // Nothing is open on first render — the panel container is parked
      // after the tile grid until a tile or a #concerns/<key> hash opens
      // one. Placing it up front keeps the DOM position correct for the
      // current viewport before any click or hash repositions it.
      this.place(this.activeKey());
      this.onHashChange();
    }

    activeKey() {
      const activeButton = this.buttons.find((button) => button.classList.contains('on'));
      return activeButton ? activeButton.dataset.concernKey : null;
    }

    place(key) {
      const button = key ? this.buttons.find((b) => b.dataset.concernKey === key) : null;
      if (this.mq.matches && button) {
        button.insertAdjacentElement('afterend', this.routes);
        this.routes.classList.add('v4-croutes--inline');
      } else {
        this.nav.insertAdjacentElement('afterend', this.routes);
        this.routes.classList.remove('v4-croutes--inline');
      }
    }

    show(key, options) {
      options = options || {};
      const known = this.buttons.some((b) => b.dataset.concernKey === key);
      if (!known) key = null;

      this.routes.classList.toggle('v4-croutes--open', !!key);
      this.place(key);

      this.buttons.forEach((b) => {
        const isOn = b.dataset.concernKey === key;
        b.classList.toggle('on', isOn);
        b.setAttribute('aria-expanded', isOn ? 'true' : 'false');
      });
      this.panels.forEach((p) => {
        p.classList.toggle('on', p.dataset.concernKey === key);
      });

      if (options.scroll && key) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        requestAnimationFrame(() => {
          this.routes.scrollIntoView({ behavior, block: 'nearest' });
        });
      }
    }

    onTileClick(event) {
      const button = event.target.closest('button[data-concern-key]');
      if (!button) return;
      const key = button.classList.contains('on') ? null : button.dataset.concernKey;
      this.show(key);
      const hash = key ? '#concerns/' + key : '#concerns';
      if (history.replaceState) history.replaceState(null, '', hash);
    }

    onHashChange() {
      const hash = window.location.hash || '';
      const match = hash.match(/^#concerns(?:\/([a-z0-9-]+))?/);
      if (!match) return;
      this.show(match[1] || null, { scroll: !!match[1] });
    }
  }

  customElements.define('skin-concerns-nav', SkinConcernsNav);
}
